import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { useDialog } from '@loadsmart/loadsmart-ui'
import { Layout, SpinnerWheel } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { type ShipmentTrackingStatus } from '../../domain/Shipment'
import DeleteLocationDialog from './DeleteLocationDialog'
import { LocationPingItem } from './LocationPingItem'
import { LocationPingsCard } from './LocationPingsCard'
import { LocationPingsEmptyState } from './LocationPingsEmptyState'
import { useShipmentLocationsContext } from './ShipmentLocationsContext'

export type LocationPingsProps = {
  readonly shipmentUUID: string
  readonly trackingLink?: string | null
  readonly trackingStatus: ShipmentTrackingStatus
}

export function LocationPings({ shipmentUUID, trackingLink, trackingStatus }: LocationPingsProps) {
  const { locations, getLocationsStatus, deleteLocationStatus, deleteLocation, refetchLocations } =
    useShipmentLocationsContext()
  const [locationUUID, setLocationUUID] = useState<string | undefined>('')
  const { open, show, hide } = useDialog({ open: false })

  const handleOpenDeleteDialog = useCallback(
    (locationUUID?: string) => {
      setLocationUUID(locationUUID)
      show()
    },
    [show]
  )

  const handleDeleteLocation = useCallback(async () => {
    if (!locationUUID) return

    try {
      await deleteLocation({ shipmentUUID, locationUUID })

      toast.success('Location deleted successfully')

      hide()

      await refetchLocations()
    } catch {
      toast.error('Could not delete location, try again later')
    }
  }, [locationUUID, shipmentUUID, deleteLocation, hide, refetchLocations])

  if (getLocationsStatus === RequestStatus.Pending) {
    return (
      <LocationPingsCard trackingLink={trackingLink}>
        <Layout.Stack align="center" justify="center" style={{ height: '100%' }}>
          <SpinnerWheel size="30px" aria-label="loading ping locations" />
        </Layout.Stack>
      </LocationPingsCard>
    )
  }

  if (trackingStatus === 'tracking') {
    return (
      <LocationPingsCard trackingLink={trackingLink}>
        <Layout.Stack style={{}}>
          {locations?.map(location => (
            <LocationPingItem
              key={location.uuid}
              location={location}
              onDeleteLocation={handleOpenDeleteDialog}
            />
          ))}
        </Layout.Stack>
        <DeleteLocationDialog
          isOpen={open}
          onCancel={hide}
          onDelete={handleDeleteLocation}
          isLoading={deleteLocationStatus === RequestStatus.Pending}
        />
      </LocationPingsCard>
    )
  }

  return (
    <LocationPingsCard trackingLink={trackingLink}>
      <Layout.Stack align="center" justify="center" style={{ height: '100%' }}>
        <LocationPingsEmptyState trackingStatus={trackingStatus} trackingLink={trackingLink} />
      </Layout.Stack>
    </LocationPingsCard>
  )
}
